import { updateTransmissionReport } from "app/services/transmission/report";
import {
  setAutoScrollPageNumber,
  setEvokeReportNotes,
  setFileToAttachSelected,
  setFileToAttachToReport,
  setOriginalEvokeReportNotes,
  setOriginalFileToAttachToReport,
  setShowActionMenu,
  setTransmissionReportFileEnableCache,
  setTransmissionReportPdf,
  setTransmissionReportPdfUrl,
  setUpdatedTransmissionReportFile,
} from "app/store/transmission/slice/transmissionSlice";
import { countPagesInPDF, getFileExtension } from "app/utils/file";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { manufacturerMappings } from "static/constants";
import { convertBytesToMB, createFormData } from "utils/common";
import { getTotalPagesInPDF, mergeMultiplePdfs, splitPDF } from "utils/pdf";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { getFormattedDate } from "utils/date";
import { isValidArray } from "utils/array";
import useAuthorization from "hooks/useAuthorization";

const useInsertPage = () => {
  const dispatch = useDispatch();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [newPagePosition, setNewPagePosition] = useState("end");
  const [newPageAfterPageNumber, setNewPageAfterPageNumber] = useState(1);
  const [originalPDFTotalPages, setOriginalPDFTotalPages] = useState(0);
  const [newPDFTotalPages, setNewPDFTotalPages] = useState(0);
  const [isConfirmingReportUpdate, setIsConfirmingReportUpdate] =
    useState(false);
  const {
    reportId,
    reportDetail,
    evokeReportNotes,
    autoScrollPageNumber,
    fileToAttachToReport,
    isReportNoteEditable,
    transmissionReportPdf,
    updatedTransmissionReportFile,
  } = useSelector((state) => state.transmission);

  const { currentUser } = useSelector((state) => state.auth);

  const { accessedAccountMappings } = useAuthorization();

  const onClose = () => {
    dispatch(setShowActionMenu(false));
    onCancel();
  };

  const onCancel = () => {
    dispatch(setFileToAttachSelected(false));
    dispatch(setUpdatedTransmissionReportFile(null));
  };

  useEffect(() => {
    const fetchTotalPages = async () => {
      setOriginalPDFTotalPages(await getTotalPagesInPDF(transmissionReportPdf));
    };
    if (transmissionReportPdf) {
      fetchTotalPages();
    }
  }, [transmissionReportPdf]);

  const handleFileUpload = async (uploadedFiles) => {
    if (!uploadedFiles.length) {
      return showErrorToast("No file(s) selected.");
    }

    const hasWrongFileExtension = uploadedFiles.some(
      (file) => getFileExtension(file.name) !== "pdf"
    );

    if (hasWrongFileExtension) {
      return showErrorToast("Only PDF files are allowed.");
    }

    const totalFileSize = uploadedFiles.reduce(
      (acc, currentValue) => acc + currentValue.size,
      0
    );

    const pagePromises = uploadedFiles.map((pdf) => getTotalPagesInPDF(pdf));
    const pagesArray = await Promise.all(pagePromises);
    const newPDFTotalPages = pagesArray.reduce((sum, pages) => sum + pages, 0);
    setNewPDFTotalPages(newPDFTotalPages);

    if (convertBytesToMB(totalFileSize) > 5) {
      return showErrorToast("The total file size cannot be greater than 5MB");
    }

    let files;
    if (
      newPagePosition === "page" &&
      newPageAfterPageNumber !== originalPDFTotalPages
    ) {
      const pdfSplite = await splitPDF(
        transmissionReportPdf,
        newPageAfterPageNumber
      );
      const [splitFirstHalfPDF, splitSecondHalfPDF] = pdfSplite;
      files = [splitFirstHalfPDF, ...uploadedFiles, splitSecondHalfPDF];
    } else {
      files =
        newPagePosition !== "start"
          ? [transmissionReportPdf, ...uploadedFiles]
          : [...uploadedFiles, transmissionReportPdf];
    }

    const { pdfUrl, fileNames, pdfBlob } = await mergeMultiplePdfs(files);
    const updatedTransmissionReport = new File([pdfBlob], fileNames[0], {
      type: pdfBlob.type,
    });

    dispatch(setUpdatedTransmissionReportFile(updatedTransmissionReport));

    const pageInsertFrom =
      newPagePosition === "end"
        ? (await countPagesInPDF(transmissionReportPdf)) + 1
        : newPagePosition === "start"
          ? 1
          : newPageAfterPageNumber + 1;
    const pageInsertTo =
      newPagePosition === "end"
        ? await countPagesInPDF(updatedTransmissionReport)
        : newPagePosition === "start"
          ? 1
          : newPageAfterPageNumber + newPDFTotalPages;

    const fileToAttach = {
      pdfUrl,
      pageInsertTo,
      pageInsertFrom,
      newPDFTotalPages,
      fileName: fileNames[0],

      report: updatedTransmissionReport,
      modifiedBy: currentUser.name,
      modifiedAt: getFormattedDate(new Date(), "M/D/YYYY h:mmA"),
    };

    if (pageInsertFrom === pageInsertTo) {
      delete fileToAttach.pageInsertTo;
    }

    dispatch(setFileToAttachToReport(fileToAttach));
    dispatch(setFileToAttachSelected(true));
  };

  const handleUpdateNewReport = async () => {
    try {
      setIsConfirmingReportUpdate(true);
      const { folder: manufacturerFolder } = manufacturerMappings.find(
        (manufacturer) => manufacturer.name === reportDetail.manufacturer
      );

      const { folder: accountFolder } = accessedAccountMappings.find(
        (account) => account.name === reportDetail.account
      );

      const payload = {
        accountFolder,
        manufacturerFolder,
        name: fileToAttachToReport.fileName,
        ...fileToAttachToReport,
      };

      if (newPagePosition !== "end" && isValidArray(evokeReportNotes)) {
        payload.updatedEvokeNotes = JSON.stringify(getUpdateEvokeNotes());
      }

      const formData = createFormData(payload);
      const response = await updateTransmissionReport(reportId, formData);
      if (response) {
        dispatch(setTransmissionReportPdf(updatedTransmissionReportFile));
        dispatch(setTransmissionReportPdfUrl(fileToAttachToReport.pdfUrl));
        dispatch(setTransmissionReportFileEnableCache(false));

        if (needsPageNumUpdate(autoScrollPageNumber)) {
          const parsedEvokeNotes = JSON.parse(payload.updatedEvokeNotes);
          const newAutoScrollPageNum = autoScrollPageNumber + newPDFTotalPages;
          dispatch(setEvokeReportNotes(parsedEvokeNotes));
          dispatch(setOriginalEvokeReportNotes(parsedEvokeNotes));
          dispatch(setAutoScrollPageNumber(newAutoScrollPageNum));
        }

        showSuccessToast("New pages have been added to the report.");
        dispatch(setOriginalFileToAttachToReport(fileToAttachToReport));
        onClose();
      }
    } catch (error) {
      showErrorToast(error?.message);
    } finally {
      setShowConfirmModal(false);
      setIsConfirmingReportUpdate(false);
    }
  };

  const getUpdateEvokeNotes = () => {
    const updatedEvokeReportNotes = evokeReportNotes.map((reportNote) => {
      return {
        ...reportNote,
        content: reportNote.content.map((note) => {
          if (note.pageNumber !== null && needsPageNumUpdate(note.pageNumber)) {
            return {
              ...note,
              pageNumber: note.pageNumber + newPDFTotalPages,
            };
          }
          return note;
        }),
      };
    });
    return updatedEvokeReportNotes;
  };

  const needsPageNumUpdate = (existingPageNumber) => {
    return (
      newPagePosition === "start" ||
      (newPagePosition === "page" &&
        existingPageNumber > newPageAfterPageNumber)
    );
  };

  return {
    newPagePosition,
    showConfirmModal,
    isReportNoteEditable,
    originalPDFTotalPages,
    newPageAfterPageNumber,
    isConfirmingReportUpdate,

    onClose,
    dispatch,
    onCancel,
    handleFileUpload,
    setNewPagePosition,
    setShowConfirmModal,
    setNewPageAfterPageNumber,
    handleUpdateNewReport,
  };
};

export default useInsertPage;
